import { Box, MenuItem, Paper, Skeleton, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { PromotionView } from '@/api/generated';
import type { GrantedCard } from '@/api/rateCards';
import { moneyIn } from '@/components/moneyIn';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme';
import { costBasisOf, costRows, type CostBasis, type PromotionDiscount } from './promotionMath';

export interface PromotionCostCardProps {
  promotions: PromotionView[];
  selected: PromotionView | undefined;
  /** The adopted card: its `baseMinor` is the retail, its `baseWholesaleMinor` the wholesale. */
  tenantCard: GrantedCard | null;
  onSelect: (id: number) => void;
}

export function PromotionCostCard({
  promotions,
  selected,
  tenantCard,
  onSelect,
}: PromotionCostCardProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const basis = costBasisOf(tenantCard);
  const ordered = [
    ...promotions.filter((promo) => promo.status === 'ACTIVE'),
    ...promotions.filter((promo) => promo.status !== 'ACTIVE'),
  ];

  return (
    <Paper sx={{ p: 2.5, minWidth: 0 }}>
      <Typography variant="subtitle1" component="h2">
        {t('promotions.cost.title')}
      </Typography>
      <Typography variant="body2" sx={{ color: tokens.text2, mb: 2 }}>
        {t('promotions.cost.subtitle')}
      </Typography>
      {basis === null ? (
        <Typography>{t('promotions.cost.unavailable')}</Typography>
      ) : (
        <>
          {ordered.length > 0 ? (
            <TextField
              select
              fullWidth
              margin="dense"
              label={t('promotions.cost.pick')}
              value={selected?.id ?? ''}
              onChange={(e) => onSelect(Number(e.target.value))}
              sx={{ mb: 2 }}
            >
              {ordered.map((promo) => (
                <MenuItem key={promo.id} value={promo.id} disabled={promo.id === undefined}>
                  {promo.code}
                  {promo.name ? ` — ${promo.name}` : ''}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          {selected && !costRows(selected, basis.retail, basis.wholesale).applicable ? (
            <Typography>{t('promotions.cost.notApplicable')}</Typography>
          ) : selected ? (
            <CostGrid promo={selected} basis={basis} locale={locale} />
          ) : null}
        </>
      )}
    </Paper>
  );
}

/**
 * The New promotion page's live preview (TMMRSL-0073): the same cost math on the draft's discount.
 * While the draft has no usable discount it says what it needs, never zeros.
 */
export function PromotionCostPreview({
  draft,
  basis,
  loading,
}: {
  /** The draft's discount, or `null` while it is incomplete. */
  draft: PromotionDiscount | null;
  basis: CostBasis | null;
  /** The two cards are still loading. */
  loading: boolean;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  let body;
  if (loading) {
    body = (
      <Box role="status" aria-label={t('common.loading')}>
        <Skeleton variant="rounded" height={120} />
      </Box>
    );
  } else if (basis === null) body = <Typography>{t('promotions.cost.unavailable')}</Typography>;
  else if (draft === null) body = <Typography>{t('promotions.cost.needsDiscount')}</Typography>;
  else if (!costRows(draft, basis.retail, basis.wholesale).applicable) {
    body = <Typography>{t('promotions.cost.notApplicable')}</Typography>;
  } else body = <CostGrid promo={draft} basis={basis} locale={locale} />;
  return (
    <Paper sx={{ p: 2.5, minWidth: 0 }}>
      <Typography variant="subtitle1" component="h2">
        {t('promotions.cost.previewTitle')}
      </Typography>
      <Typography variant="body2" sx={{ color: tokens.text2, mb: 2 }}>
        {t('promotions.cost.subtitle')}
      </Typography>
      {body}
    </Paper>
  );
}

function CostGrid({
  promo,
  basis,
  locale,
}: {
  promo: PromotionDiscount;
  basis: CostBasis;
  locale: string;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const { currency } = basis;
  const rows = costRows(promo, basis.retail, basis.wholesale);
  // Inside a grid cell a missing amount is the dash; the sentence is for the not-ready card only.
  const dash = t('statements.unavailable');
  const money = (minor: number) => moneyIn(minor, currency, locale, dash);
  const minus = (minor: number) => {
    const value = money(minor);
    return value === dash ? dash : `−${value}`;
  };
  const items = [
    {
      key: 'retail',
      label: t('promotions.cost.retail'),
      value: money(rows.retail),
      note: t('promotions.cost.retailNote'),
      color: tokens.text,
    },
    {
      key: 'discount',
      label: t('promotions.cost.discount'),
      value: minus(rows.discount),
      note: t('promotions.cost.discountNote'),
      color: tokens.errorFg,
    },
    {
      key: 'wholesale',
      label: t('promotions.cost.wholesale'),
      value: minus(rows.wholesale),
      note: t('promotions.cost.wholesaleNote'),
      color: tokens.errorFg,
    },
    {
      key: 'afterDiscount',
      label: t('promotions.cost.afterDiscount'),
      value: money(rows.retail - rows.discount),
      note: t('promotions.cost.afterDiscountNote'),
      color: tokens.brand,
    },
  ];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
      {items.map((item) => (
        <Box
          key={item.key}
          sx={{ border: '1px solid', borderColor: tokens.line, borderRadius: 1, p: 1.5 }}
        >
          <Typography variant="overline" sx={{ color: tokens.text2 }}>
            {item.label}
          </Typography>
          <Typography variant="kpi" component="p" sx={{ color: item.color }}>
            {item.value}
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.text2 }}>
            {item.note}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
